class ScrollManager {
  constructor() {
    this.scrollListeners = new Map();
    this.isScrolling = false;
    this.scrollTimeout = null;
    this.lastScrollY = 0;
    this.scrollDirection = 'down';
    this.throttleDelay = 16;
    this.debounceDelay = 100;

    this.init();
  }

  init() {
    this.setupScrollOptimization();
    this.setupVariantPickerScrollFix();
    this.setupThemeEditorFlickerFix();
    this.setupHeaderIntegration();
  }

  setupScrollOptimization() {
    this.optimizedScrollHandler = this.throttle((event) => {
      this.handleScroll(event);
    }, this.throttleDelay);

    this.cleanupExistingScrollListeners();
    window.addEventListener('scroll', this.optimizedScrollHandler, { passive: true });
  }

  setupVariantPickerScrollFix() {
    const originalVariantSelects = window.customElements.get('variant-selects');
    if (originalVariantSelects) {
      const originalRenderProductInfo = originalVariantSelects.prototype.renderProductInfo;

      originalVariantSelects.prototype.renderProductInfo = function () {
        this.classList.add('variant-changing');

        const result = originalRenderProductInfo.call(this);

        setTimeout(() => {
          this.classList.remove('variant-changing');
        }, 100);

        return result;
      };
    }

    document.addEventListener('click', (event) => {
      const variantPicker = event.target.closest('variant-selects');
      if (variantPicker) {
        event.stopPropagation();
      }
    });
  }

  setupThemeEditorFlickerFix() {
    if (!window.Shopify?.designMode) return;

    const originalHideProductModal = window.hideProductModal;

    window.hideProductModal = () => {
      const modals = document.querySelectorAll('product-modal[open]');
      modals.forEach((modal) => {
        modal.style.transition = 'opacity 0.2s ease-in-out';
        modal.style.opacity = '0';
        setTimeout(() => {
          modal.hide();
          modal.style.transition = '';
          modal.style.opacity = '';
        }, 200);
      });
    };

    document.addEventListener('shopify:section:select', (event) => {
      const section = event.target;
      if (section) {
        section.style.transition = 'all 0.2s ease-in-out';
        section.classList.add('section-selecting');

        setTimeout(() => {
          section.classList.remove('section-selecting');
          section.style.transition = '';
        }, 200);
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const section = event.target;
      if (section) {
        section.classList.remove('section-selecting');
      }
    });
  }

  handleScroll(event) {
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
    this.scrollDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';
    this.lastScrollY = currentScrollY;
    this.isScrolling = true;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
      this.onScrollEnd();
    }, this.debounceDelay);

    this.scrollListeners.forEach((listener, key) => {
      try {
        listener(currentScrollY, this.scrollDirection, event);
      } catch (error) {
        console.warn(`Scroll listener ${key} failed:`, error);
      }
    });
  }

  onScrollEnd() {
    this.scrollListeners.forEach((listener, key) => {
      if (typeof listener.onScrollEnd === 'function') {
        try {
          listener.onScrollEnd();
        } catch (error) {
          console.warn(`Scroll end listener ${key} failed:`, error);
        }
      }
    });
  }

  addScrollListener(key, listener) {
    this.scrollListeners.set(key, listener);
  }

  removeScrollListener(key) {
    this.scrollListeners.delete(key);
  }

  cleanupExistingScrollListeners() {
    const scrollElements = document.querySelectorAll('[data-scroll-listener], .scroll-handler');

    scrollElements.forEach((element) => {
      const newElement = element.cloneNode(true);
      if (element.parentNode) {
        element.parentNode.replaceChild(newElement, element);
      }
    });
  }

  throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;

    return function (...args) {
      const currentTime = Date.now();

      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(
          () => {
            func.apply(this, args);
            lastExecTime = Date.now();
          },
          delay - (currentTime - lastExecTime)
        );
      }
    };
  }

  debounce(func, delay) {
    let timeoutId;

    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  setupHeaderIntegration() {
    const checkHeaderHandler = () => {
      if (window.HeaderScrollHandler) {
        this.addScrollListener('header-integration', (scrollY, direction) => {});
      } else {
        setTimeout(checkHeaderHandler, 100);
      }
    };

    checkHeaderHandler();
  }

  getScrollMetrics() {
    return {
      isScrolling: this.isScrolling,
      lastScrollY: this.lastScrollY,
      scrollDirection: this.scrollDirection,
      listenerCount: this.scrollListeners.size,
    };
  }
}

const scrollManager = new ScrollManager();

window.ScrollManager = scrollManager;

const scrollFixCSS = `
  /* Prevent scroll stopping on variant picker */
  .variant-changing {
    pointer-events: none;
  }

  .variant-changing * {
    pointer-events: none;
  }

  /* Smooth transitions for theme editor */
  .section-selecting {
    transition: all 0.2s ease-in-out !important;
  }

  /* Prevent flickering during animations */
  .scroll-trigger {
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  /* Optimize scroll performance */
  .product-form__input,
  .variant-selects {
    contain: layout style paint;
  }

  /* Fix for sticky elements */
  .header--sticky {
    will-change: transform;
    transform: translateZ(0);
  }
`;

const style = document.createElement('style');
style.textContent = scrollFixCSS;
document.head.appendChild(style);

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    window.removeEventListener('scroll', scrollManager.optimizedScrollHandler);
  } else {
    window.addEventListener('scroll', scrollManager.optimizedScrollHandler, { passive: true });
  }
});
